import { useState, useCallback, useRef, useEffect } from 'react'
import { Upload, Download, X } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'
import { useUploadList } from '@/entities/list'
import type { CampaignShortItem, ConflictPolicy } from '@intone/shared/types'

// Columns to exclude from template (system variables)
const COLUMNS_TO_EXCLUDE = [
  'agent_name',
  'current_year',
  'current_month',
  'current_day',
  'current_week_day',
  'current_time_24_hour',
  'greeting',
]

// Extract {{variable}} patterns from prompts
function extractVariablesFromPrompts(prompts: string[]): string[] {
  const allFields = new Set<string>()
  prompts.forEach((prompt) => {
    const matches = prompt.match(/\{\{([^}]+)\}\}/g)
    if (matches) {
      matches.forEach((match) => {
        const word = match.replace(/[{}]/g, '').trim()
        allFields.add(word)
      })
    }
  })
  return Array.from(allFields).filter((col) => !COLUMNS_TO_EXCLUDE.includes(col))
}

type UploadProgressStatus = {
  status: 'processing' | 'complete' | 'error'
  total: number
  processed: number
  created: number
  updated: number
  skipped: number
  message?: string
}

function startUploadTracking(
  listId: number,
  total: number,
  api: <T>(url: string, options?: RequestInit) => Promise<T>,
  queryClient: { invalidateQueries: (opts: { queryKey: string[] }) => void },
) {
  const toastId = `upload-${listId}`
  toast.loading(`Processing ${total} leads…`, { id: toastId, duration: Infinity })

  const poll = setInterval(async () => {
    try {
      const result = await api<UploadProgressStatus>(`/lists/${listId}/upload-status`)

      if (result.status === 'complete') {
        clearInterval(poll)
        queryClient.invalidateQueries({ queryKey: ['lists'] })
        toast.success(
          `Upload complete — ${result.created} created, ${result.updated} updated, ${result.skipped} skipped`,
          { id: toastId, duration: 8000 },
        )
      } else if (result.status === 'error') {
        clearInterval(poll)
        queryClient.invalidateQueries({ queryKey: ['lists'] })
        toast.error(result.message || 'Upload failed', { id: toastId, duration: 8000 })
      } else {
        toast.loading(
          `Processing… ${result.processed} / ${result.total} leads`,
          { id: toastId, duration: Infinity },
        )
      }
    } catch {
      clearInterval(poll)
    }
  }, 2000)
}

interface DialogAddListProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaigns: CampaignShortItem[]
  selectedCampaignId: number | null
}

export function DialogAddList({
  open,
  onOpenChange,
  campaigns,
  selectedCampaignId,
}: DialogAddListProps) {
  const uploadList = useUploadList()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const apiClient = useApiClient()

  const [name, setName] = useState('')
  const [campaignId, setCampaignId] = useState<number | null>(selectedCampaignId)
  const [source, setSource] = useState('')
  const [conflictPolicy, setConflictPolicy] = useState<ConflictPolicy>('skip')
  const [skipNonUs, setSkipNonUs] = useState(false)
  const [overrideStatus, setOverrideStatus] = useState('NEW')
  const [file, setFile] = useState<File | null>(null)
  const [campaignColumns, setCampaignColumns] = useState<string[]>([])
  const queryClient = useQueryClient()

  // Fetch campaign LLM prompts to extract variables
  useEffect(() => {
    if (!campaignId) {
      setCampaignColumns([])
      return
    }

    const fetchPrompts = async () => {
      try {
        const prompts = await apiClient<string[]>(`/llms/campaign/${campaignId}/prompts`)
        const columns = extractVariablesFromPrompts(prompts)
        setCampaignColumns(columns)
      } catch {
        setCampaignColumns([])
      }
    }

    fetchPrompts()
  }, [campaignId, apiClient])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file')
        return
      }
      setFile(selectedFile)
    }
  }, [])

  const handleRemoveFile = useCallback(() => {
    setFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleDownloadTemplate = useCallback(() => {
    // Build columns: Phone is always first, then campaign-specific columns (excluding phone to avoid duplicates)
    const filteredCampaignColumns = campaignColumns.filter(col => col.toLowerCase() !== 'phone')
    const columns = ['Phone', ...filteredCampaignColumns]
    // Create sample row
    const sampleValues = columns.map((col) => {
      if (col.toLowerCase() === 'phone') return '12345678901'
      return `Sample ${col}`
    })
    const csvContent = `${columns.join(',')}\n${sampleValues.join(',')}`
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'list_template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }, [campaignColumns])

  const handleClose = useCallback(() => {
    setName('')
    setSource('')
    setFile(null)
    setOverrideStatus('NEW')
    onOpenChange(false)
  }, [onOpenChange])

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      toast.error('List name is required')
      return
    }
    if (!campaignId) {
      toast.error('Please select a campaign')
      return
    }
    if (!file) {
      toast.error('Please select a CSV file')
      return
    }

    const formData = new FormData()
    formData.append('name', name.trim())
    formData.append('campaign_id', String(campaignId))
    formData.append('conflict_policy', conflictPolicy)
    formData.append('skip_non_us', String(skipNonUs))
    if (overrideStatus.trim()) {
      formData.append('override_status', overrideStatus.trim())
    }
    if (source.trim()) {
      formData.append('source', source.trim())
    }
    formData.append('file', file)

    try {
      const result = await uploadList.mutateAsync(formData)
      handleClose()

      if (result.processing) {
        startUploadTracking(result.list_id, result.count, apiClient, queryClient)
      } else {
        toast.success(
          `Upload complete — ${result.created ?? 0} created, ${result.updated ?? 0} updated, ${result.skipped ?? 0} skipped`,
        )
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Upload failed')
    }
  }, [name, campaignId, file, conflictPolicy, skipNonUs, overrideStatus, source, uploadList, handleClose, apiClient, queryClient])

  // Update campaign when prop changes
  if (selectedCampaignId !== null && campaignId === null) {
    setCampaignId(selectedCampaignId)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
          <DialogTitle>Upload New List</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="space-y-4">
            {/* Campaign Selection */}
            <div className="space-y-1.5">
              <Label>Campaign *</Label>
              <Select
                value={campaignId ? String(campaignId) : undefined}
                onValueChange={(v) => setCampaignId(parseInt(v, 10))}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select a campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {campaignColumns.filter(col => col.toLowerCase() !== 'phone').length > 0 && (
                <p className="text-detail text-muted-foreground mt-1">
                  Your AI agents use the following columns: {campaignColumns.filter(col => col.toLowerCase() !== 'phone').join(', ')}.
                </p>
              )}
            </div>

            {/* List Name */}
            <div className="space-y-1.5">
              <Label>List Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={63}
                placeholder="Enter list name"
                className="h-8"
              />
            </div>

            {/* Source */}
            <div className="space-y-1.5">
              <Label>Source (optional)</Label>
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                maxLength={255}
                placeholder="e.g., Website, Facebook, etc."
                className="h-8"
              />
            </div>

            {/* Conflict Policy */}
            <div className="space-y-1.5">
              <Label>Duplicate Phone Policy</Label>
              <Select
                value={conflictPolicy}
                onValueChange={(v) => setConflictPolicy(v as ConflictPolicy)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip existing leads</SelectItem>
                  <SelectItem value="override">Update existing leads</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Skip Non-US */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="skipNonUs"
                checked={skipNonUs}
                onCheckedChange={(checked) => setSkipNonUs(checked === true)}
              />
              <Label htmlFor="skipNonUs" className="font-normal cursor-pointer">
                Skip non-US phone numbers
              </Label>
            </div>

            {/* Override Status */}
            <div className="space-y-1.5">
              <Label>Default Disposition</Label>
              <Input
                value={overrideStatus}
                onChange={(e) => setOverrideStatus(e.target.value)}
                placeholder="NEW"
                className="h-8"
              />
            </div>

            {/* File Upload */}
            <div className="space-y-1.5">
              <Label>CSV File *</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                {file ? (
                  <div className="flex items-center justify-between">
                    <span className="text-detail">{file.name}</span>
                    <Button variant="ghost" size="sm" onClick={handleRemoveFile} className="h-6 w-6 p-0">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className="cursor-pointer text-blue-600 hover:text-blue-700"
                    >
                      <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                      <span className="text-detail">Click to upload CSV</span>
                    </label>
                  </div>
                )}
              </div>
              <p className="text-detail text-muted-foreground">
                CSV must include a &quot;Phone&quot; column. Other columns will be stored as lead data.
              </p>
            </div>

            {/* Template Download */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="w-full h-8"
            >
              <Download className="h-3 w-3 mr-2" />
              Download CSV Template
            </Button>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={handleClose} className="h-8">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={uploadList.isPending} className="h-8">
                {uploadList.isPending ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}